import { useEffect, useRef } from "react";
import { usePlayerStore } from "@/lib/player/player.store";
import type { ListenedSegment } from "@/lib/podcast/podcastPlaybackProgress.service";

/**
 * useSegmentTracking Hook
 *
 * Tracks which segments of a podcast have been actually listened to (not skipped).
 * This is different from just tracking currentTime - it records continuous listening
 * segments and can detect when users skip around.
 *
 * Features:
 * - Records continuous listening segments
 * - Detects and handles seeking/skipping
 * - Merges adjacent segments automatically
 * - Returns current segments for syncing with backend
 *
 * @example
 * ```tsx
 * const listenedSegments = useSegmentTracking();
 * // Returns: [{start: 0, end: 120}, {start: 300, end: 420}]
 * // User listened to 0:00-2:00 and 5:00-7:00 but skipped 2:00-5:00
 * ```
 */
export function useSegmentTracking(): ListenedSegment[] {
	const podcastId = usePlayerStore((state) => state.podcastId);
	const isPlaying = usePlayerStore((state) => state.isPlaying);
	const currentTime = usePlayerStore((state) => state.currentTime);

	// Store segments per podcast
	const segmentsByPodcastRef = useRef<Map<string, ListenedSegment[]>>(
		new Map(),
	);

	// Track the current active segment being listened to
	const activeSegmentRef = useRef<{
		podcastId: string;
		start: number;
		lastUpdate: number;
	} | null>(null);

	// Threshold for detecting a "seek" vs continuous playback (in seconds)
	const SEEK_THRESHOLD = 3;

	// Minimum segment length to record (avoid noise from brief pauses)
	const MIN_SEGMENT_LENGTH = 2;

	useEffect(() => {
		if (!podcastId || !isPlaying) {
			// If paused or no podcast, finalize the current segment
			if (activeSegmentRef.current && podcastId) {
				finalizeSegment(podcastId);
			}
			return;
		}

		const active = activeSegmentRef.current;

		// Check if we're continuing the same segment or starting a new one
		if (
			active &&
			active.podcastId === podcastId &&
			Math.abs(currentTime - active.lastUpdate) <= SEEK_THRESHOLD
		) {
			// Continue current segment
			active.lastUpdate = currentTime;
		} else {
			// Finalize old segment if it exists and meets minimum length
			if (active && active.podcastId === podcastId) {
				finalizeSegment(podcastId);
			}

			// Start new segment
			activeSegmentRef.current = {
				podcastId,
				start: currentTime,
				lastUpdate: currentTime,
			};
		}
	}, [isPlaying, currentTime, podcastId]);

	// Finalize segment on unmount or podcast change
	useEffect(() => {
		const currentPodcastId = podcastId;

		return () => {
			if (currentPodcastId && activeSegmentRef.current) {
				finalizeSegment(currentPodcastId);
			}
		};
	}, [podcastId]);

	/**
	 * Finalize the current active segment and add it to the segments list
	 */
	function finalizeSegment(forPodcastId: string) {
		const active = activeSegmentRef.current;
		if (!active || active.podcastId !== forPodcastId) return;

		const segmentLength = active.lastUpdate - active.start;

		// Only record segments that meet minimum length
		if (segmentLength >= MIN_SEGMENT_LENGTH) {
			const newSegment: ListenedSegment = {
				start: active.start,
				end: active.lastUpdate,
			};

			// Get existing segments for this podcast
			const existing = segmentsByPodcastRef.current.get(forPodcastId) || [];

			// Add new segment and merge overlapping ones
			const updated = mergeSegments([...existing, newSegment]);

			segmentsByPodcastRef.current.set(forPodcastId, updated);
		}

		activeSegmentRef.current = null;
	}

	/**
	 * Merge overlapping or adjacent segments
	 */
	function mergeSegments(segments: ListenedSegment[]): ListenedSegment[] {
		if (segments.length === 0) return [];

		// Sort by start time
		const sorted = [...segments].sort((a, b) => a.start - b.start);

		// Merge overlapping segments
		const merged: ListenedSegment[] = [];
		for (const segment of sorted) {
			if (merged.length === 0) {
				merged.push({ ...segment });
				continue;
			}

			const last = merged[merged.length - 1];
			// If segments overlap or are adjacent (within 2 seconds), merge them
			if (segment.start <= last.end + 2) {
				last.end = Math.max(last.end, segment.end);
			} else {
				merged.push({ ...segment });
			}
		}

		return merged;
	}

	// Return segments for current podcast
	if (!podcastId) return [];

	const segments = segmentsByPodcastRef.current.get(podcastId) || [];

	// If there's an active segment, include it as a temporary segment
	if (
		activeSegmentRef.current &&
		activeSegmentRef.current.podcastId === podcastId
	) {
		const tempSegment: ListenedSegment = {
			start: activeSegmentRef.current.start,
			end: activeSegmentRef.current.lastUpdate,
		};
		return mergeSegments([...segments, tempSegment]);
	}

	return segments;
}
